import type { ElicitRequestedSchema } from "@qw/qw-common";

export type ElicitSchemaLike = Pick<ElicitRequestedSchema, "properties"> & { required?: string[] };

export interface ElicitConfirmationConfig {
  kind: "simple" | "with-details";
  supplementalFieldKeys: string[];
}

export interface ElicitationDisplayState {
  headerLabel: string;
  icon: "check_circle" | "tooltip_2";
  statusText: string | null;
  showEntries: boolean;
  isConfirmation: boolean;
}

export function getElicitConfirmationConfig(requestedSchema: ElicitSchemaLike): ElicitConfirmationConfig | null {
  const confirmProperty = requestedSchema.properties?.confirm;

  if (confirmProperty?.type !== "boolean") {
    return null;
  }

  const supplementalFieldKeys = Object.keys(requestedSchema.properties ?? {}).filter((key) => key !== "confirm");

  return {
    kind: supplementalFieldKeys.length > 0 ? "with-details" : "simple",
    supplementalFieldKeys,
  };
}

export function getElicitationDisplayState(
  schema: ElicitSchemaLike | null | undefined,
  response: { action?: string; content?: Record<string, unknown> } | null | undefined
): ElicitationDisplayState {
  const confirmationConfig = schema ? getElicitConfirmationConfig(schema) : null;
  const isConfirmation = confirmationConfig !== null;
  const action = response?.action;
  const content = response?.content ?? {};
  const confirmValue = content.confirm;

  if (isConfirmation && action === "accept" && typeof confirmValue === "boolean") {
    return {
      headerLabel: confirmValue
        ? "Confirmed"
        : confirmationConfig.kind === "with-details"
          ? "Needs Changes"
          : "Not Confirmed",
      icon: confirmValue ? "check_circle" : "tooltip_2",
      statusText: null,
      showEntries: true,
      isConfirmation,
    };
  }

  if (action === "cancel") {
    return {
      headerLabel: isConfirmation ? "Skipped" : "Information Requested",
      icon: "tooltip_2",
      statusText: "Skipped",
      showEntries: false,
      isConfirmation,
    };
  }

  if (action === "decline") {
    return {
      headerLabel: isConfirmation ? "Declined" : "Information Requested",
      icon: "tooltip_2",
      statusText: "Declined",
      showEntries: false,
      isConfirmation,
    };
  }

  return {
    headerLabel: isConfirmation ? "Confirmation Requested" : "Answered",
    icon: isConfirmation ? "tooltip_2" : "check_circle",
    statusText: null,
    showEntries: true,
    isConfirmation,
  };
}

export function getElicitationFieldLabel(key: string, title: string | undefined, isConfirmation: boolean): string {
  if (isConfirmation && key === "confirm") {
    return "Decision";
  }

  return title || key;
}

export function getElicitationFieldDisplayValue(key: string, value: unknown, isConfirmation: boolean): string | null {
  if (value === undefined || value === null) {
    return null;
  }

  if (isConfirmation && key === "confirm" && typeof value === "boolean") {
    return value ? "Confirm" : "Deny";
  }

  return null;
}
